import React, { useContext } from 'react';
import { NavLink, useLocation, useNavigate, Link } from 'react-router-dom';
import { FiHome, FiShoppingCart, FiPackage, FiUsers, FiPieChart, FiSettings, FiLogOut, FiX, FiTruck, FiCreditCard, FiMinusCircle, FiZap, FiLock } from 'react-icons/fi';
import { toast } from 'react-toastify';
import AuthContext from '../context/AuthContext';
import logo from '../assets/logo.png';

const Sidebar = ({ isSidebarOpen, setSidebarOpen, openUpgradeModal }) => {
  const { logout, user } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();

  // DEBUG: Affiche l'utilisateur dans la console pour vérifier les droits
  if (user) {
    console.log("Sidebar User Info:", { username: user.username, role: user.role, isAdmin: user.isAdmin });
  }

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const closeSidebar = () => {
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  const menuItems = [
    { path: '/dashboard', icon: FiHome, label: 'Tableau de bord', exact: true },
    { path: '/dashboard/sales', icon: FiShoppingCart, label: 'Vente / Caisse', id: 'tour-step-sidebar-sales' },
    { path: '/dashboard/stock', icon: FiPackage, label: 'Stock', id: 'tour-step-sidebar-stock' },
    { path: '/dashboard/expenses', icon: FiMinusCircle, label: 'Dépenses' },
    { path: '/dashboard/clients', icon: FiUsers, label: 'Clients' },
    { path: '/dashboard/debts', icon: FiCreditCard, label: 'Dettes Clients' },
    { path: '/dashboard/suppliers', icon: FiTruck, label: 'Fournisseurs' },
    { path: '/dashboard/supplier-debts', icon: FiCreditCard, label: 'Dettes Fournisseurs' },
    { path: '/dashboard/reports', icon: FiPieChart, label: 'Rapports' },
    { path: '/dashboard/team', icon: FiUsers, label: 'Équipe' },
    { path: '/dashboard/profile', icon: FiSettings, label: 'Paramètres' },
  ];

  const getRequiredPlan = (path) => {
    switch (path) {
      case '/dashboard/debts':
      case '/dashboard/suppliers':
      case '/dashboard/reports':
        return 'pro';
      case '/dashboard/supplier-debts':
      case '/dashboard/team':
        return 'premium';
      default:
        return 'essentiel';
    }
  };

  const isAccessAllowed = (userPlan, requiredPlan) => {
    const levels = { 'essentiel': 1, 'pro': 2, 'premium': 3 };
    const uLevel = levels[userPlan || 'essentiel'] || 1;
    const rLevel = levels[requiredPlan] || 1;
    return uLevel >= rLevel;
  };

  return (
    <>
      {/* Mobile Overlay */}
      <div 
        className={`fixed inset-0 z-20 bg-black/50 backdrop-blur-sm transition-opacity lg:hidden ${isSidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-30 w-72 bg-white border-r border-gray-100 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 flex flex-col shadow-2xl lg:shadow-none ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        
        {/* Logo Area */}
        <div className="h-16 flex items-center justify-between lg:justify-center px-6 border-b border-gray-50">
            <Link to="/dashboard" onClick={closeSidebar}>
                <img src={logo} alt="Jaapand-e" className="h-12 w-auto" />
            </Link>
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden p-2 rounded-full hover:bg-gray-100 text-gray-500 transition-colors">
                <FiX size={24} />
            </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-6 px-4 space-y-1.5 no-scrollbar">
            {menuItems.map((item) => {
                const isActive = item.exact 
                    ? location.pathname === item.path 
                    : location.pathname.startsWith(item.path);
                
                const requiredPlan = getRequiredPlan(item.path);
                const allowed = isAccessAllowed(user?.plan, requiredPlan);
                
                return (
                    <NavLink
                        key={item.path}
                        to={item.path}
                        id={item.id}
                        onClick={(e) => {
                            if (!allowed) {
                                e.preventDefault();
                                const message = requiredPlan === 'premium' 
                                    ? "Cette fonctionnalité est réservée aux membres Premium." 
                                    : "Passez au plan Pro pour débloquer cette section.";
                                toast.info(message, { icon: <FiLock /> });
                                openUpgradeModal();
                            } else {
                                closeSidebar();
                            }
                        }}
                        className={({ isActive }) => `
                            flex items-center px-4 py-3.5 rounded-2xl transition-all duration-200 group font-medium
                            ${isActive && allowed
                                ? 'bg-brand-blue text-white shadow-lg shadow-blue-500/30 translate-x-1' 
                                : 'text-gray-500 hover:bg-blue-50 hover:text-brand-blue hover:translate-x-1'
                            }
                            ${!allowed ? 'opacity-60 cursor-not-allowed grayscale-[0.5]' : ''}
                        `}
                    >
                        <div className="relative mr-4">
                            <item.icon className={`h-5 w-5 transition-colors ${isActive && allowed ? 'text-white' : 'text-gray-400 group-hover:text-brand-blue'}`} />
                            {!allowed && (
                                <div className="absolute -top-1.5 -right-1.5 bg-gray-100 rounded-full p-0.5 border border-white">
                                    <FiLock className="w-2.5 h-2.5 text-gray-500" />
                                </div>
                            )}
                        </div>
                        <span className="text-sm tracking-wide">{item.label}</span>
                        {isActive && allowed && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white/50"></div>}
                        {!allowed && (
                            <div className="ml-auto">
                                <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border ${
                                    requiredPlan === 'premium' ? 'bg-yellow-50 text-yellow-600 border-yellow-100' : 'bg-blue-50 text-blue-600 border-blue-100'
                                }`}>
                                    {requiredPlan === 'premium' ? 'Prem' : 'Pro'}
                                </span>
                            </div>
                        )}
                    </NavLink>
                );
            })}

            {/* Lien Admin - Visible uniquement si admin */}
            {(user?.role === 'admin' || !!user?.isAdmin || user?.username === 'Ndiaye') && (
                <NavLink
                    to="/dashboard/admin"
                    onClick={closeSidebar}
                    className={({ isActive }) => `
                        flex items-center px-4 py-3.5 rounded-2xl transition-all duration-200 group font-medium mt-4 border-t-2 border-gray-100
                        ${isActive 
                            ? 'bg-gray-800 text-white shadow-lg' 
                            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                        }
                    `}
                >
                    <div className="relative mr-4"><FiLock className="h-5 w-5" /></div>
                    <span className="text-sm tracking-wide font-bold">Panel Admin</span>
                </NavLink>
            )}
        </nav>

        {/* Footer / Upgrade */}
        <div className="p-6 border-t border-gray-50 bg-gray-50/30">
            {user?.plan === 'essentiel' && (
                <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-5 mb-6 text-white relative overflow-hidden shadow-lg group cursor-pointer" onClick={openUpgradeModal}>
                    <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10 blur-2xl group-hover:bg-white/20 transition-all"></div>
                    <h4 className="font-bold text-sm mb-1 flex items-center"><FiZap className="mr-2 text-yellow-400 fill-current"/> Passer Pro</h4>
                    <p className="text-xs text-gray-300 mb-3 leading-relaxed">Débloquez les SMS et la gestion d'équipe.</p>
                    <div className="w-full py-2 bg-white/10 backdrop-blur-sm text-white rounded-lg text-xs font-bold text-center border border-white/10 group-hover:bg-white group-hover:text-gray-900 transition-all">
                        Voir les offres
                    </div>
                </div>
            )}

            <button 
                onClick={handleLogout}
                className="flex items-center justify-center w-full px-4 py-3 text-sm font-bold text-red-600 bg-red-50 rounded-xl hover:bg-red-100 hover:shadow-sm transition-all"
            >
                <FiLogOut className="mr-2 h-5 w-5" />
                Déconnexion
            </button>
            
            <div className="mt-4 text-center">
                <p className="text-[10px] font-bold text-gray-300 uppercase tracking-widest">Jaapand-e v1.0</p>
            </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;